import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiService } from "../services/api";
import type {
  CreateReportJobRequest,
  ReportJob,
  ReportJobListResponse,
} from "../types/reportJob";

interface ReportJobState {
  jobs: ReportJob[];
  loading: boolean;
  creating: boolean;
  refreshing: boolean;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  fetchJobs: (params?: Record<string, unknown>) => Promise<void>;
  createJob: (request: CreateReportJobRequest) => Promise<ReportJob>;
  refreshJobStatus: (jobId: number) => Promise<ReportJob>;
  retryJob: (jobId: number) => Promise<ReportJob>;
  deleteJob: (jobId: number) => Promise<void>;
}

export const useReportJobStore = create<ReportJobState>()(
  devtools(
    (set, get) => ({
      jobs: [],
      loading: false,
      creating: false,
      refreshing: false,
      totalCount: 0,
      currentPage: 1,
      totalPages: 1,
      itemsPerPage: 20,

      fetchJobs: async (params = { page: 1, limit: 20 }) => {
        set({ loading: true });
        try {
          const response = (await apiService.getReportJobs(
            params
          )) as ReportJobListResponse;
          set({
            jobs: response.jobs,
            totalCount: response.pagination.total,
            currentPage: response.pagination.page,
            totalPages: response.pagination.totalPages,
            itemsPerPage: response.pagination.limit,
            loading: false,
          });
        } catch (error) {
          console.error("Error fetching report jobs:", error);
          set({ loading: false });
          throw error;
        }
      },

      createJob: async (request: CreateReportJobRequest) => {
        set({ creating: true });
        try {
          const job = (await apiService.createReportJob(request)) as ReportJob;
          set((state) => ({
            jobs: [job, ...state.jobs],
            creating: false,
          }));
          return job;
        } catch (error) {
          set({ creating: false });
          throw error;
        }
      },

      refreshJobStatus: async (jobId: number) => {
        set({ refreshing: true });
        try {
          const updated = (await apiService.refreshReportJobStatus(
            jobId
          )) as ReportJob;
          const jobs = get().jobs.map((job) => (job.id === jobId ? updated : job));
          set({ jobs, refreshing: false });
          return updated;
        } catch (error) {
          set({ refreshing: false });
          throw error;
        }
      },

      retryJob: async (jobId: number) => {
        set({ refreshing: true });
        try {
          const updated = (await apiService.retryReportJob(jobId)) as ReportJob;
          const jobs = get().jobs.map((job) => (job.id === jobId ? updated : job));
          set({ jobs, refreshing: false });
          return updated;
        } catch (error) {
          set({ refreshing: false });
          throw error;
        }
      },

      deleteJob: async (jobId: number) => {
        await apiService.deleteReportJob(jobId);
        set((state) => ({
          jobs: state.jobs.filter((job) => job.id !== jobId),
        }));
      },
    }),
    { name: "report-job-store" }
  )
);
